import { Outlet, useSearchParams } from "react-router-dom";

import { type BatchOutcome, useBatchOutcomes, useBatches } from "../api/queries";
import { linkTo } from "../api/links";
import type { Batch } from "../api/types";
import {
  DetailPlaceholder,
  ListRow,
  LoadMore,
  MasterDetail,
  RowField,
  RowFields,
} from "../components/MasterDetail";
import {
  Badge,
  Empty,
  ErrorBox,
  Guid,
  OutcomeSummary,
  Spinner,
  StalePending,
  StatusBadge,
  Time,
  shorten,
} from "../components/primitives";

const STATUSES = ["", "pending", "applied", "failed"];
const WINDOWS = [
  { value: "24", label: "24h" },
  { value: "168", label: "7d" },
  { value: "720", label: "30d" },
  { value: "2160", label: "90d" },
];

const FILTER_KEYS = ["root_guid", "driver_guid", "device_guid"] as const;

/** Filters live in the URL so a view can be pasted into a ticket. */
export function BatchesPage() {
  const [params, setParams] = useSearchParams();

  const filters = {
    status: params.get("status") ?? undefined,
    root_guid: params.get("root_guid") ?? undefined,
    driver_guid: params.get("driver_guid") ?? undefined,
    device_guid: params.get("device_guid") ?? undefined,
    window_hours: Number(params.get("window_hours") ?? 168),
    limit: 50,
  };
  const scoped = FILTER_KEYS.some((key) => filters[key]);

  const query = useBatches(filters);
  const batches = query.data?.pages.flatMap((page) => page.batches) ?? [];
  const outcomes = useBatchOutcomes(batches.map((batch) => batch.batch_guid));

  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  }

  return (
    <MasterDetail
      title="Inbox batches"
      count={batches.length ? `${batches.length}${query.hasNextPage ? "+" : ""}` : undefined}
      filters={
        <>
          <select
            className="field-select"
            value={filters.status ?? ""}
            onChange={(event) => update("status", event.target.value)}
          >
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {status || "any status"}
              </option>
            ))}
          </select>
          <select
            className="field-select"
            value={String(filters.window_hours)}
            onChange={(event) => update("window_hours", event.target.value)}
            disabled={scoped}
            title={scoped ? "An identity filter reads the full retention, not a window" : undefined}
          >
            {WINDOWS.map((window) => (
              <option key={window.value} value={window.value}>
                {window.label}
              </option>
            ))}
          </select>
          {FILTER_KEYS.map((key) =>
            filters[key] ? (
              <button key={key} type="button" className="btn mono" onClick={() => update(key, "")}>
                {key.replace("_guid", "")}: {shorten(filters[key])} ✕
              </button>
            ) : null,
          )}
        </>
      }
      footer={
        scoped
          ? "Filtered by identity — reading the full retention."
          : "Bounded by the window above."
      }
      detail={<Outlet />}
    >
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && batches.length === 0 && (
        <Empty>
          No batches in this window.
          <div className="mt-1 text-xs text-faint">
            An empty inbox is also what a driver who never reached the engine looks like.
          </div>
        </Empty>
      )}

      {batches.map((batch) => (
        <BatchListItem key={batch.batch_guid} batch={batch} outcome={outcomes(batch.batch_guid)} />
      ))}

      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </MasterDetail>
  );
}

function BatchListItem({ batch, outcome }: { batch: Batch; outcome: BatchOutcome }) {
  return (
    <ListRow to={`/batches/${batch.batch_guid}`}>
      <div className="flex items-center gap-2">
        <StatusBadge status={batch.status} counts={outcome.counts} />
        <OutcomeSummary {...outcome} pending={batch.status === "pending"} />
        <Guid value={batch.batch_guid} />
        <span className="ml-auto flex shrink-0 items-center gap-1">
          <StalePending batch={batch} />
          {batch.attempts > 0 && <Badge tone="warn">{batch.attempts}×</Badge>}
          {batch.connection_status !== "activated" && (
            <Badge tone="warn">{batch.connection_status ?? "no conn"}</Badge>
          )}
        </span>
      </div>
      <RowFields>
        <RowField label="events">
          <span className="text-muted">{batch.events.length}</span>
        </RowField>
        <RowField label={batch.root_type}>
          <Guid value={batch.root_guid} to={linkTo.root(batch.root_type, batch.root_guid)} />
        </RowField>
        <RowField label="driver">
          <Guid value={batch.driver_guid} to={linkTo.driver(batch.driver_guid)} />
        </RowField>
        <RowField label="device">
          <Guid value={batch.device_guid} to={linkTo.device(batch.device_guid)} />
        </RowField>
        <RowField label="received">
          <Time value={batch.received_at} />
        </RowField>
      </RowFields>
      {batch.last_error && (
        <p className="mono mt-1 truncate text-[11px] text-bad/90" title={batch.last_error}>
          {batch.last_error}
        </p>
      )}
    </ListRow>
  );
}

export function BatchesPlaceholder() {
  return (
    <DetailPlaceholder>
      Pick a batch to see the events exactly as the device sent them, and what the apply decided
      about each one.
    </DetailPlaceholder>
  );
}
